import {
  ClassProvider,
  ConstructorProvider,
  ExistingProvider,
  FactoryProvider,
  Injector,
  StaticClassProvider,
  TypeProvider,
  ValueProvider,
} from '@angular/core';
import {
  APP_ZONES,
  ContainerType,
  DeviceScrollbarVisibility,
  NewUiZoneWidgetConfig,
  ProfileStateModel,
  ProviderClass,
  SavingsProviders,
  SupportedDevice,
  UiPageConfig,
  UiZoneWidgetConfig,
  UserInfoType,
} from '@finxone-platform/shared/sys-config-types';
import Handlebars from 'handlebars';

import { ActivatedRoute, ParamMap } from '@angular/router';

import { Store } from '@ngxs/store';
import { map, Observable, take } from 'rxjs';
import { GetOrganisation } from '../../lib/actions/organisation.action';
import { SetActiveOrgId } from '../../lib/actions/user-profile.action';
import { ConfigService } from '../../lib/services/config-service/config-service.service';
import { ProjectSettingsState } from '../../lib/state/project-settings.state';
import { ProfileState } from '../../lib/state/user-profile.state';
import { isAuthenticatedRoute } from '../../lib/utils/auth-checks.utils';
import {
  setOverFlowAutoOnBodyTag,
  setOverFlowHiddenOnBodyTag,
} from '../../lib/utils/scrolling-utils/scrolling.utils';
import { GenericHandleBarModel } from '../../lib/utils/template/model-builder.utils';
import { formatNameAsUrl } from '../../lib/utils/zone-url.utils';
import { PageConfigurationProvider } from './page-configuration.provider';
import { ZonePageUrlProvider } from './zone-url-provider';

export function createInjector(
  widget: UiZoneWidgetConfig,
  page: UiPageConfig,
  injector: Injector,
  configService: ConfigService,
  zonePageUrlProvider: ZonePageUrlProvider,
  user: UserInfoType,
  userProfile?: ProfileStateModel,
  // Try remove as this is used for page states which we don't really use anymore
  widgetSection?: {
    widgetName: string;
    widgetIndex?: number;
    isSectionPopup?: boolean;
  },
  extraProviders?: (
    | ValueProvider
    | ExistingProvider
    | StaticClassProvider
    | ConstructorProvider
    | FactoryProvider
    | TypeProvider
    | ClassProvider
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | any
  )[],
): Injector {
  if (!page.pageConfiguration || Object.keys(page.pageConfiguration).length === 0) {
    console.error('[DynamicZoneHolder] Page Configuration is empty at Injection.');
  }

  const compAttri = widget.attributes;
  const updatedCompAttri = widgetSection
    ? {
        widgetName: widgetSection.widgetName,
        widgetIndex: widgetSection.widgetIndex,
        isSectionPopup: widgetSection.isSectionPopup,
        ...compAttri,
      }
    : {
        ...compAttri,
      };

  return Injector.create({
    providers: [
      {
        provide: ProviderClass,
        useValue: {
          attri: updatedCompAttri,
          uniqueId: widget?.uniqueId,
          widgetLayout: widget,
          widgetConfig: widget,
          id: user ? user.id : '',
          userProfileData: user ? user : {},
          userProfile: userProfile ?? {},
        },
      },
      {
        provide: ZonePageUrlProvider,
        useValue: zonePageUrlProvider,
      },
      {
        provide: ConfigService,
        useValue: configService,
      },
      {
        provide: PageConfigurationProvider,
        useValue: page.pageConfiguration,
      },
    ].concat(extraProviders ?? []),
    parent: injector,
  });
}

export function createChildWidgetInjector(
  widget: NewUiZoneWidgetConfig,
  injector: Injector,
  dataIn: ProviderClass<object>,
  extraProviders?: (
    | ValueProvider
    | ExistingProvider
    | StaticClassProvider
    | ConstructorProvider
    | FactoryProvider
    | TypeProvider
    | ClassProvider
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | any
  )[],
) {
  return Injector.create({
    providers: [
      {
        provide: ProviderClass,
        useValue: {
          attri: widget.attributes,
          uniqueId: widget?.uniqueId,
          widgetLayout: widget,
          widgetConfig: widget,
          id: dataIn.id,
          userProfileData: dataIn.userProfileData,
          userProfile: dataIn.userProfile,
        },
      },
    ].concat(extraProviders ?? []),
    parent: injector,
  });
}

export function resetStylesOnWebApp() {
  const element = document.getElementById('web-app');
  if (element) {
    element.style.removeProperty('background');
    element.style.removeProperty('backgroundSize');
    element.style.removeProperty('opacity');
    element.style.removeProperty('background');
    element.style.backgroundColor = 'var(--color-background)';

    const cssOverride = window.document.getElementById('page-css-override');
    if (cssOverride) {
      cssOverride.parentNode?.removeChild(cssOverride);
    }
  }
}

export function getRouteParams(route: ActivatedRoute): Observable<{
  zoneUrlInput: string;
  pageUrlInput: string;
  roleInput: string;
  subscriptionInput: string;
  idInput: string;
}> {
  return route.paramMap.pipe(
    map((params: ParamMap) => {
      const pageOrModal = (params.get('modal-page') ? params.get('modal-page') : params.get('page')) ?? '';
      if (params.get('zone') === null || pageOrModal === null) {
        throw Error('No zone or page specified via path or input');
      } else {
        return {
          zoneUrlInput: params.get('zone') ?? '',
          pageUrlInput: pageOrModal ?? '',
          roleInput: params.get('role') ?? '',
          subscriptionInput: params.get('subscription') ?? '',
          idInput: params.get('id') ?? '',
        };
      }
    }),
  );
}

export function fetchProfileAndSetOrgInfoBasedOnZone(
  profile$: Observable<ProfileStateModel>,
  store: Store,
  zone: string,
) {
  return profile$.pipe(
    take(2),
    map((profileData) => {
      let count = 0;
      if (profileData?.activeOrganisationId && isAuthenticatedRoute(window.location.pathname)) {
        store.dispatch(new SetActiveOrgId(profileDataOrgOrLocalOrg(profileData)));
      }
      if (zone === formatNameAsUrl(APP_ZONES.ORGANISATION_VERIFICATION)) {
        const organisationId = profileData?.activeOrganisationId ?? '';
        if (organisationId && count === 0) {
          store.dispatch(new GetOrganisation(profileDataOrgOrLocalOrg(profileData)));
          count++;
        }
      }

      return profileData ? profileData : undefined;
    }),
  );
}

function profileDataOrgOrLocalOrg(profileData: ProfileStateModel) {
  const localActiveOrganisationId = localStorage.getItem('activeOrganisationId');

  // Should load last selected org if a valid choice exists in local storage
  // This should be set in the user profile state when settinging active org
  // if set in locla then
  if (localActiveOrganisationId) {
    // check a valid choice

    const allowedOrgIds = profileData.orgList?.map((org) => org.id) ?? [];

    if (allowedOrgIds.includes(localActiveOrganisationId)) {
      return localActiveOrganisationId;
    }
  }

  return profileData?.activeOrganisationId ?? '';
}

const originalWidgetLayouts = new WeakMap<object, UiZoneWidgetConfig[]>();

export function hideWidgetsIfNeededWithCallback(
  modelToCheck: GenericHandleBarModel,
  widgetLayout: UiZoneWidgetConfig[],
  callback: () => void,
) {
  // Get or store original layout
  if (!originalWidgetLayouts.has(widgetLayout)) {
    originalWidgetLayouts.set(widgetLayout, JSON.parse(JSON.stringify(widgetLayout)));
  }
  const originalLayout = originalWidgetLayouts.get(widgetLayout)!;

  // Find all widgets that have a hide condition
  const widgetsToHide = getWidgetsToHide(originalLayout);

  if (!widgetsToHide.length) {
    callback();
    return;
  }

  const results = widgetsToHide.map((w) =>
    evaluateCondition(w.attributes.hideCondition as string, modelToCheck),
  );

  const hiddenWidgetIds = new Set<string | number>();

  widgetsToHide.forEach((w, index) => {
    try {
      const result = results[index];
      const booleanResult = /true/.test(result);

      if (booleanResult) {
        hiddenWidgetIds.add(w.uniqueId);
      }
    } catch (error) {
      console.error(`Failed to evaluate hide condition on widget: ${w.uniqueId} with error: ${error}`);
    }
  });
  // Recursively filter out hidden widgets
  const filteredLayout = filterHiddenWidgets(originalLayout, hiddenWidgetIds);

  widgetLayout.length = 0;
  widgetLayout.push(...filteredLayout);

  callback();
}

// Function to recursively find widgets that have a hide condition
export function getWidgetsToHide(widgets: UiZoneWidgetConfig[]): UiZoneWidgetConfig[] {
  return widgets.reduce((hiddenWidgets: UiZoneWidgetConfig[], widget: NewUiZoneWidgetConfig) => {
    if (widget.attributes?.hideCondition) {
      hiddenWidgets.push(widget); // Add widget to the list if it has a hide condition
    }
    // Recursively check child components for hide conditions
    if (widget.childComponents?.length) {
      const childWidgets = widget.childComponents.flatMap((childArray) =>
        Array.isArray(childArray) ? getWidgetsToHide(childArray) : getWidgetsToHide([childArray]),
      );
      hiddenWidgets.push(...childWidgets);
    }
    return hiddenWidgets;
  }, []);
}

// Function to recursively filter out widgets that should be hidden
export function filterHiddenWidgets(
  widgets: UiZoneWidgetConfig[],
  hiddenWidgetIds: Set<string | number>,
): UiZoneWidgetConfig[] {
  return widgets
    .map((widget: NewUiZoneWidgetConfig) => {
      // If the widget's uniqueId is in the hiddenWidgetIds set, return null (remove it)
      if (hiddenWidgetIds.has(widget.uniqueId)) {
        return null;
      }
      // Recursively filter child components
      const newChildComponents = widget?.childComponents
        ?.map((childArray) => {
          return Array.isArray(childArray)
            ? filterHiddenWidgets(childArray, hiddenWidgetIds)
            : filterHiddenWidgets([childArray], hiddenWidgetIds)[0];
        })
        .filter(Boolean); // Remove any null or undefined child components

      // Return the updated widget with the filtered child components
      return { ...widget, childComponents: newChildComponents };
    })
    .filter(Boolean) as UiZoneWidgetConfig[]; // Remove null values from the final list
}

export function isBodyScrollingEnabled(
  device: SupportedDevice,
  pageConfig: UiPageConfig,
  projectScrollbarSettings?: DeviceScrollbarVisibility,
): void {
  const isDesktop = device === 'desktop';
  const isBodyScrollingEnabled = pageConfig.pageConfiguration?.isBodyScrollingEnabled;
  const scrollbarsVisible = projectScrollbarSettings?.[device];
  if ((isDesktop && isBodyScrollingEnabled === false) || (!isDesktop && isBodyScrollingEnabled !== true)) {
    // Remove the class here too, otherwise it stays on body.classList when navigating
    // from a page that had it enabled to one where scrolling is disabled.
    document.body.classList.remove('show-scrollbars');
    setOverFlowHiddenOnBodyTag();
  } else {
    // Toggle rather than only add, so the class is cleared when this page's
    // scrollbarsVisible is false but body.classList already has it from a previous page.
    document.body.classList.toggle('show-scrollbars', !!scrollbarsVisible);
    setOverFlowAutoOnBodyTag();
  }
}

/**
 * Caches the background color of the currently active non-modal page.
 *
 * This value is updated whenever a standard page is rendered. It is used as a fallback
 * for modal pages: if a modal is opened without an explicit background color of parent page,
 * it applies this cached value to ensure visual consistency with the underlying page.
 */
let lastSavedBackgroundColor = '';

export function applyPageConfigStyles(
  element: HTMLElement,
  pageConfig: UiPageConfig,
  isModalPage = false,
  isWorkflow = false,
) {
  element.style.cssText = '';
  if (
    pageConfig?.pageConfiguration?.isAdvancedCssActive &&
    pageConfig?.pageConfiguration?.advancedCss?.length
  ) {
    element.style.cssText = pageConfig?.pageConfiguration?.advancedCss;
    return;
  }

  if (
    isWorkflow &&
    pageConfig?.containerType === ContainerType.PAGE &&
    pageConfig?.footerLayoutEnabled &&
    pageConfig?.footerConfiguration?.enableStickyFooter
  ) {
    element.style.setProperty('overflow', 'hidden', 'important');
  }

  // set the page background color value into lastSavedBackgroundColor
  if (!isModalPage && pageConfig?.containerType === ContainerType.PAGE) {
    element.style.backgroundColor = lastSavedBackgroundColor =
      pageConfig.pageConfiguration?.backgroundColor ?? 'var(--color-background)';
  }

  // if element does not have any background color set, it takes the last saved background color from the parent page and applies into background when user enters the modal screen. When we go to page screen again, it updates the current page background color and it does not come to this point.
  if (!element.style.backgroundColor && isModalPage) {
    element.style.backgroundColor = lastSavedBackgroundColor;
  }

  if (
    !isModalPage &&
    pageConfig?.containerType !== ContainerType.MODAL &&
    !pageConfig.pageConfiguration?.backgroundImage?.image
  ) {
    const prevImageElement = document.getElementById('backgroundImageWrapper');
    if (prevImageElement) {
      prevImageElement.remove();
    }
  }

  if (
    !isModalPage &&
    pageConfig?.containerType !== ContainerType.MODAL &&
    pageConfig?.pageConfiguration?.backgroundImage?.image
  ) {
    createBackgroundImageWrapperElement(element, pageConfig);
  }

  if (
    pageConfig?.pageConfiguration?.backgroundGradient &&
    pageConfig?.pageConfiguration?.backgroundGradient?.length > 0
  ) {
    element.style.background = pageConfig?.pageConfiguration?.backgroundGradient ?? 'unset';
    element.style.height = pageConfig?.pageConfiguration?.backgroundGradient ? '100vh' : 'unset';
  }
}

export function createBackgroundImageWrapperElement(element: HTMLElement, pageConfig: UiPageConfig) {
  const prevImageElement = document.getElementById('backgroundImageWrapper');
  if (prevImageElement) {
    prevImageElement.remove();
  }
  if (!document.getElementById('backgroundImageWrapper')) {
    const pseudoElement = document.createElement('div');
    pseudoElement.id = 'backgroundImageWrapper';
    pseudoElement.style.content = '""';
    pseudoElement.style.position = 'fixed';
    pseudoElement.style.top = '0';
    pseudoElement.style.left = '0';
    pseudoElement.style.height = '100%';
    pseudoElement.style.width = '100%';
    pseudoElement.style.backgroundImage = pageConfig?.pageConfiguration?.backgroundImage?.image ?? 'unset';
    pseudoElement.style.backgroundColor = 'transparent';
    pseudoElement.style.opacity =
      pageConfig?.pageConfiguration?.backgroundImage?.opacity.toString() ?? 'unset';
    pseudoElement.style.backgroundSize = pageConfig?.pageConfiguration?.backgroundSize ?? 'auto';
    pseudoElement.style.backgroundAttachment = 'fixed';
    pseudoElement.style.backgroundPosition = 'center';
    pseudoElement.style.zIndex = '-1';
    if (pageConfig?.pageConfiguration?.backgroundPosition) {
      pseudoElement.style.backgroundPositionX =
        pageConfig?.pageConfiguration?.backgroundPosition.x ?? 'unset';
      pseudoElement.style.backgroundPositionY =
        pageConfig?.pageConfiguration?.backgroundPosition.y ?? 'unset';
    }
    element.appendChild(pseudoElement);
  }
}

export function buildCssFontRules(fontsConfig: {
  h1: {
    'font-family': string;
    'font-weight': string;
  };
  h2: {
    'font-family': string;
    'font-weight': string;
  };

  h3: {
    'font-family': string;
    'font-weight': string;
  };
  bodyFont: {
    'font-family': string;
    'font-weight': string;
  };
}): string {
  return `
    #web-app h1 { ${
      fontsConfig?.h1['font-weight'] ? `font-weight: ${fontsConfig?.h1['font-weight']}` : ''
    }; ${fontsConfig?.h1['font-family'] ? `font-family: ${fontsConfig?.h1['font-family']}` : ''} }
    #web-app h2 { ${
      fontsConfig?.h2['font-weight'] ? `font-weight: ${fontsConfig?.h2['font-weight']}` : ''
    }; ${fontsConfig?.h2['font-family'] ? `font-family: ${fontsConfig?.h2['font-family']}` : ''} }
    #web-app h3 { ${
      fontsConfig?.h3['font-weight'] ? `font-weight: ${fontsConfig?.h3['font-weight']}` : ''
    }; ${fontsConfig?.h3['font-family'] ? `font-family: ${fontsConfig?.h3['font-family']}` : ''} }
    #web-app body { ${
      fontsConfig?.bodyFont['font-weight'] ? `font-weight: ${fontsConfig?.bodyFont['font-weight']}` : ''
    }; ${fontsConfig?.bodyFont['font-family'] ? `font-family: ${fontsConfig?.bodyFont['font-family']}` : ''} }
  `;
}

export const urlSearchParamsToJson = (searchParams: URLSearchParams): Record<string, string> => {
  const params: Record<string, string> = {};

  // Iterate through all the search parameters
  searchParams?.forEach((value, key) => {
    params[key] = value;
  });

  return params;
};

export function evaluateCondition(condition: string, modelToCheck: GenericHandleBarModel) {
  const templateFunction = Handlebars.compile(`{{#if (${condition})}}true{{else}}false{{/if}}`);
  return templateFunction(modelToCheck);
}

export function isUserVerifiedAndIsSavingsProductsProvider(store: Store): boolean {
  const tokenExists = !!sessionStorage.getItem('token');
  const profile = store.selectSnapshot(ProfileState.getProfile);
  const provider = store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);
  if (!provider) {
    return false;
  }
  const isSavingsProvider = SavingsProviders.includes(provider);
  return tokenExists && profile?.kycVerificationStatus === 'success' && isSavingsProvider;
}
